import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFormValues, getFormMeta, change } from "redux-form";

import { SOCIAL_WIZARD } from "widgets/forms/list";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { loadFacebookPages } from "reducers/Social";
import { buildFacebookPages } from "selectors/Social";

/**
 * Takes an object of `baseFields`, keyed by field name, used in social creative forms,
 * and applies appropriate universal modifications, and returns an array of field props,
 * which can be mapped over to display the form.
 *
 * The returned data can be further modified for specific fields with special cases.
 * This hook should only be for general case modifications.
 */
export function useSocialFields(baseFields = {}, formProps = {}){
	const {
		isViewOnly = false,
		doVersionsExist = false,
		showPristineErrors = false,
		isEditMode = false,
	} = formProps;

	return Object.values(baseFields).map((fieldProps) => {
		const { placeholder } = fieldProps || {};
		const props = {
			...fieldProps,
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(placeholder && isViewOnly){
			// Clear placeholder if form is view only
			props.placeholder = "";
		}

		if(doVersionsExist){
			props.dynamicToggleDisabled = true;
			props.variableNameInputDisabled = true;
			props.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if (fieldProps.name === 'frameNumber') {
			props.isEditMode = isEditMode;
		}

		return props;
	});
}

/**
 * Loads Facebook/Instagram accounts and selects the first option by default, if field hasn't
 * already been touched, or if editing an existing Creative.
 * Used with Flashtalking connections.
*/
export function useDefaultAccounts({
	libraryId,
	isEditMode,
	adType,
}){
	const dispatch = useDispatch();

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD));
	const meta = useSelector(getFormMeta(SOCIAL_WIZARD));

	const { facebookPages, instagramAccounts } = useSelector(buildFacebookPages);

	useEffect(() => {
		dispatch(loadFacebookPages(libraryId));
	}, [libraryId]);

	useEffect(() => {
		if (isEditMode) { return; }

		const selectedFacebookPage = formValues?.[adType]?.facebookPage;
		const isTouchedFacebookPage = meta?.[adType]?.facebookPage?.touched;

		const selectedInstagramAccount = formValues?.[adType]?.instagramAccount;
		const isTouchedInstagramAccount = meta?.[adType]?.instagramAccount?.touched;

		if(!selectedFacebookPage && !isTouchedFacebookPage){
			dispatch(change(SOCIAL_WIZARD, `${adType}.facebookPage`, facebookPages?.[0]));
		}
		if(!selectedInstagramAccount && !isTouchedInstagramAccount) {
			dispatch(change(SOCIAL_WIZARD, `${adType}.instagramAccount`, instagramAccounts?.[0]));
		}
	}, [facebookPages, instagramAccounts, formValues, meta]);
}
